'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePostHog } from 'posthog-js/react';
import {
  buildYouTubeLiveThumbnailUrl,
  buildYouTubeWatchUrl,
  sourceKey,
  type YouTubeSource,
} from '@/lib/livestream/youtube';
import { useLivestream } from './FloatingLivestreamProvider';

/* =============================================================================
   The "IQ Capital TV is live" pop-up.

   Figma: desktop node 9293:13332, mobile node 10413:2335 (file
   RTqa1425vVOUAEVlKfKcnt). Per the design annotations there is NO video embed:

     - desktop: a card with the broadcast's live thumbnail. The thumbnail and
       "Watch on YouTube ↗" both open the live on YouTube; X closes;
     - mobile: just the banner, no thumbnail. The whole banner opens the live
       on YouTube; X closes.

   Sizes are Figma px / 8 → rem, this project's convention (see
   app/globals.scss). Desktop is anchored 14px from the bottom-left corner of
   the 1920 frame; mobile 13px from the left and 10px from the bottom of the
   430 frame. Bottom-left because the Intercom launcher owns bottom-right.
============================================================================= */

/** Matches this project's `lg` breakpoint (tailwind.config.ts) minus 1px. */
const MOBILE_MAX_PX = 992;

/** YouTube refreshes a live broadcast's thumbnail periodically; re-fetch at this pace. */
const THUMBNAIL_REFRESH_MS = 60 * 1000;

const SHADOW = 'shadow-[0_3.5rem_7rem_-1.5rem_rgba(0,27,20,0.17)]';

export default function FloatingLivestreamCard() {
  const { live, isOpen, close } = useLivestream();

  /**
   * Mounted-ness and viewport class resolve in a SINGLE state update, to avoid
   * painting the desktop card for one frame before it swaps to the banner.
   */
  const [env, setEnv] = useState<{ isMobile: boolean } | null>(null);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`);
    const sync = () => setEnv({ isMobile: mql.matches });
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, []);

  if (!env || !isOpen || !live) return null;

  const Popup = env.isMobile ? LivestreamBanner : LivestreamCard;

  return createPortal(<Popup channelLabel={live.channelLabel} source={live.source} onClose={close} />, document.body);
}

interface PopupProps {
  channelLabel: string;
  source: YouTubeSource;
  onClose: () => void;
}

/** Which link took the viewer to YouTube — the thumbnail, the text link, or the mobile banner. */
function useTrackLinkOut(source: YouTubeSource) {
  const posthog = usePostHog();
  return (placement: 'thumbnail' | 'link' | 'banner') =>
    posthog?.capture('livestream_player_link_out', { video_id: sourceKey(source), placement });
}

/** The red "● LIVE" tag, identical on desktop and mobile. */
function LiveTag() {
  return (
    <span className="relative flex shrink-0 items-center gap-[0.75rem] rounded-[5rem] border border-solid border-[rgba(255,160,160,0.3)] bg-[rgba(255,59,59,0.16)] px-[1rem] py-[0.375rem]">
      <span aria-hidden className="relative h-[0.75rem] w-[0.75rem] shrink-0 rounded-[0.375rem] bg-[#ff3b3b]">
        <span className="absolute left-[-0.2125rem] top-[-0.2125rem] h-[1.1745rem] w-[1.1745rem] rounded-full bg-[#ff3b3b] opacity-[0.42]" />
      </span>
      <span className="whitespace-nowrap font-geist text-[1.5rem] font-semibold leading-[1.75rem] tracking-[0.035rem] text-[#ff9696]">
        LIVE
      </span>
    </span>
  );
}

/* ─── Desktop: header + live thumbnail ────────────────────────────────────── */

function LivestreamCard({ channelLabel, source, onClose }: PopupProps) {
  const trackLinkOut = useTrackLinkOut(source);
  const watchUrl = buildYouTubeWatchUrl(source);
  const videoId = source.kind === 'video' ? source.videoId : null;

  // Re-fetch the frame so the thumbnail keeps up with the broadcast. A failed
  // load hides the image (the black container stays) until the next tick retries.
  const [thumbTick, setThumbTick] = useState(0);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!videoId) return;
    const timer = window.setInterval(() => setThumbTick((t) => t + 1), THUMBNAIL_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [videoId]);

  const thumbSrc = videoId ? `${buildYouTubeLiveThumbnailUrl(videoId)}?t=${thumbTick}` : null;

  return (
    <div
      role="complementary"
      aria-label={`Livestream: ${channelLabel}`}
      className={`fixed bottom-[1.75rem] left-[1.75rem] z-[99999] flex w-[43rem] flex-col gap-[1.25rem] rounded-[0.75rem] border border-solid border-white/10 bg-[#001b14] px-[1.25rem] pb-[1.25rem] pt-[1.5rem] ${SHADOW}`}
    >
      {/* 17px tall in Figma with 20px-tall content centred in it — the overflow is intentional. */}
      <div className="flex h-[2.125rem] w-full items-center justify-between gap-[1.5rem]">
        <div className="flex shrink-0 items-center gap-[1rem]">
          <span className="whitespace-nowrap font-geist text-[1.75rem] font-medium leading-[2.5rem] tracking-[-0.02rem] text-white/80">
            {channelLabel}
          </span>
          <LiveTag />
        </div>

        <div className="flex shrink-0 items-center gap-[1.5rem]">
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLinkOut('link')}
            className="flex items-center gap-[0.5rem] whitespace-nowrap font-geist text-[1.625rem] font-normal leading-[2.5rem] text-white/90 transition-colors hover:text-white"
          >
            Watch on YouTube
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/livestream/arrow-up-right.svg" alt="" aria-hidden width={16} height={16} className="h-[2rem] w-[2rem] shrink-0" />
          </a>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close livestream"
            className="flex h-[2.5rem] w-[2.5rem] shrink-0 items-center justify-center transition-opacity hover:opacity-70"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/livestream/close.svg" alt="" aria-hidden width={20} height={20} className="h-[2.5rem] w-[2.5rem]" />
          </button>
        </div>
      </div>

      <a
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Watch ${channelLabel} live on YouTube`}
        onClick={() => trackLinkOut('thumbnail')}
        className="relative block h-[20.5rem] w-full overflow-hidden rounded-[0.5rem] bg-black"
      >
        {thumbSrc && thumbSrc !== failedSrc && (
          // A remote, cache-busted frame that changes every minute — next/image would only add a proxy hop.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbSrc}
            alt=""
            onError={() => setFailedSrc(thumbSrc)}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </a>
    </div>
  );
}

/* ─── Mobile: banner only ─────────────────────────────────────────────────── */

function LivestreamBanner({ channelLabel, source, onClose }: PopupProps) {
  const trackLinkOut = useTrackLinkOut(source);
  const watchUrl = buildYouTubeWatchUrl(source);

  return (
    <div
      role="complementary"
      aria-label={`Livestream: ${channelLabel}`}
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
      className={`fixed left-[1.625rem] z-[99999] flex w-[32rem] items-center gap-[1.25rem] overflow-hidden rounded-[0.75rem] border border-solid border-white/10 bg-[#001b14] py-[0.75rem] pl-[1.25rem] pr-[1rem] ${SHADOW}`}
    >
      {/* Whole banner is the link. A stretched overlay rather than a wrapping
          <a>, because the close button can't live inside a link. */}
      <a
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Watch ${channelLabel} live on YouTube`}
        onClick={() => trackLinkOut('banner')}
        className="absolute inset-0"
      />

      <div aria-hidden className="pointer-events-none flex shrink-0 items-center gap-[1rem]">
        <span className="whitespace-nowrap font-geist text-[1.75rem] font-medium leading-[normal] text-white/90">
          {channelLabel}
        </span>
        <LiveTag />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/livestream/arrow-up-right-mobile.svg" alt="" width={18} height={18} className="h-[2.25rem] w-[2.25rem] shrink-0" />
      </div>

      <button
        type="button"
        onClick={onClose}
        title="Close"
        aria-label="Close livestream"
        className="relative ml-auto flex shrink-0 items-center rounded-[0.25rem] bg-white/10 p-[0.5rem]"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/livestream/close.svg" alt="" aria-hidden width={20} height={20} className="h-[2.5rem] w-[2.5rem]" />
      </button>
    </div>
  );
}
